import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig";
import formatUKPhone from "../../HelperFunctions/FormatUKPhone";



async function runWithConcurrency(items, limit, worker) {
  const results = [];
  let i = 0;

  const runners = Array.from({ length: limit }, async () => {
    while (i < items.length) {
      const idx = i++;
      results[idx] = await worker(items[idx], idx);
    }
  });

  await Promise.all(runners);
  return results;
}

function formatAdviser(id, baseData = {}, personal = {}, app = {}) {
  const brandName =
    baseData?.brandName ||
    baseData?.tradingName ||
    baseData?.tradingStyle ||
    "";

  return {
    id,
    firstName: personal?.forename || baseData?.firstName || "",
    preferredName: personal?.preferredName || baseData?.preferredName || "",
    lastName: personal?.surname || baseData?.lastName || "",
    email: id,
    isAppointed: baseData?.isAppointed !== false,
    dateAppointed: baseData?.dateAppointed || "",
    status: baseData?.status || [],
    tradingStyles: baseData?.tradingStyles || [],
    regulatoryText: baseData?.regulatoryText || "",
    mobileNumber:
      formatUKPhone(personal?.mobileTelephone) ||
      formatUKPhone(personal?.homeTelephone) ||
      "",
    officeNumber: formatUKPhone(personal?.officeTelephone) || "",
    fcaNumber: personal?.fcaNumber || baseData?.fcaNumber || "",
    businessAddress: personal?.businessAddress?.fullAddress || "",
    currentAddress: personal?.currentAddress || "",
    tradingStyle: baseData?.tradingStyle || "",
    tradingName: baseData?.tradingName || "",
    isAR: baseData?.isAR || false,
    isIndroducerAR: baseData?.isIndroducerAR || false,
    businessAreas: app?.businessAreas || baseData?.businessAreas || [],
    ARBusinessAreas: app?.ARBusinessAreas || baseData?.ARBusinessAreas || [],
    primaryColor: baseData?.PrimaryColor || baseData?.primaryColor || "",
    brandName,
    logoUrl: "",
    profileUrl: baseData?.profileUrl || "",
    dob: personal?.dateOfBirth || personal?.dob || "",
    website: baseData?.website || "",
  };
}

export function subscribeAdvisersData(onValue, onError, { waitForPersonalDetails = false } = {}) {
  if (typeof onValue !== "function") return () => {};

  const advisersRef = collection(db, "Authorized Members", "Advisers", "Data");
  const entries = new Map();
  let adviserOrder = [];
  let stopped = false;

  const emit = () => {
    if (stopped) return;
    if (waitForPersonalDetails && adviserOrder.some((id) => !entries.get(id)?.personalLoaded)) return;
    onValue(
      adviserOrder
        .map((id) => entries.get(id))
        .filter(Boolean)
        .map((entry) =>
          formatAdviser(entry.id, entry.base, entry.personal, entry.app),
        ),
    );
  };

  const stopEntry = (entry) => {
    entry?.unsubscribers?.forEach((unsubscribe) => unsubscribe());
  };

  const unsubscribeAdvisers = onSnapshot(
    advisersRef,
    (snapshot) => {
      const currentIds = new Set(snapshot.docs.map((item) => item.id));

      for (const [id, entry] of entries) {
        if (!currentIds.has(id)) {
          stopEntry(entry);
          entries.delete(id);
        }
      }

      adviserOrder = snapshot.docs.map((item) => item.id);

      snapshot.docs.forEach((item) => {
        const base = item.data() || {};
        let entry = entries.get(item.id);

        if (!entry) {
          entry = {
            id: item.id,
            base,
            personal: {},
            personalLoaded: false,
            app: {},
            unsubscribers: [],
          };
          entries.set(item.id, entry);

          const personalRef = doc(
            db,
            "Authorized Members",
            "Advisers",
            "Data",
            item.id,
            "Personal Details",
            "Data",
          );
          const appRef = doc(
            db,
            "Authorized Members",
            "Advisers",
            "Data",
            item.id,
            "Application Details",
            "Data",
          );

          entry.unsubscribers = [
            onSnapshot(personalRef, (detail) => {
              entry.personal = detail.exists() ? detail.data() : {};
              entry.personalLoaded = true;
              emit();
            }, (error) => {
              if (!stopped && typeof onError === "function") onError(error);
            }),
            onSnapshot(appRef, (detail) => {
              entry.app = detail.exists() ? detail.data() : {};
              emit();
            }),
          ];
        } else {
          entry.base = base;
        }
      });

      emit();
    },
    (error) => {
      if (!stopped && typeof onError === "function") onError(error);
    },
  );

  return () => {
    stopped = true;
    unsubscribeAdvisers();
    entries.forEach(stopEntry);
    entries.clear();
  };
}

export async function exportAdvisersData(
  subcollectionNames = [],
  callback,
  options = {}
) {
  try {
    const advisersRef = collection(
      db,
      "Authorized Members",
      "Advisers",
      "Data"
    );
    const advisersSnap = await getDocs(advisersRef);

    const docs = advisersSnap.docs;

    const formatted = await runWithConcurrency(docs, 8, async (docSnap) => {
      const baseData = { id: docSnap.id, ...docSnap.data() };
      if (baseData?.isAppointed === false) return null;

      const subResults = {};

      // fetch requested subcollections for THIS adviser in parallel
      await Promise.all(
        subcollectionNames.map(async (sub) => {
          try {
            const subRef = collection(
              db,
              "Authorized Members",
              "Advisers",
              "Data",
              docSnap.id,
              sub,
            );
            const subSnap = await getDocs(subRef);
            subResults[sub] = subSnap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }));
          } catch {
            subResults[sub] = [];
          }
        }),
      );

     const personal =
       subResults["Personal Details"]?.find((d) => d.id === "Data") || {};

      const app =
        subResults["Application Details"]?.find((d) => d.id === "Data") || {};


      return formatAdviser(docSnap.id, baseData, personal, app);

    });

    const cleaned = formatted.filter(Boolean);

    if (typeof callback === "function") await callback(cleaned);
    return cleaned;
  } catch (error) {
    if (typeof callback === "function") await callback([]);
    return [];
  }
}
